import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Flag, Car, Camera, Star, Plane, Wrench, Gamepad2 } from "lucide-react";
import Nav from "@/components/Nav";
import PageBackground, { PageFooter } from "@/components/PageBackground";

const GOALS = [
  { emoji: "🏁", Icon: Flag, text: "Compete at a national level" },
  { emoji: "🏎️", Icon: Car, text: "Improve my racing skills every season" },
  { emoji: "🎥", Icon: Camera, text: "Share my motorsport journey with my community" },
  { emoji: "⭐", Icon: Star, text: "Build a welcoming community around racing and VTubing" },
];

const LOVES = [
  { emoji: "🚗", Icon: Car, text: "Cars and motorsport" },
  { emoji: "✈️", Icon: Plane, text: "Aviation" },
  { emoji: "🔧", Icon: Wrench, text: "Car modifications and mechanics" },
  { emoji: "🎮", Icon: Gamepad2, text: "Racing simulators" },
];

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
};

function SectionCard({ title, children, delay = 0, testid }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay, ease: "easeOut" }}
      data-testid={testid}
      className="rounded-[24px] border border-white/60 bg-white/55 p-6 shadow-[0_20px_60px_-12px_rgba(244,63,94,0.16)] backdrop-blur-2xl backdrop-saturate-150 sm:p-8 md:p-9"
    >
      <div className="mb-5 flex items-center gap-3">
        <span className="h-px w-8 bg-rose-300" />
        <h2 className="font-display text-xs font-semibold uppercase tracking-[0.32em] text-rose-500">
          {title}
        </h2>
      </div>
      {children}
    </motion.section>
  );
}

function BulletList({ items, testidPrefix }) {
  return (
    <motion.ul
      variants={stagger}
      initial="hidden"
      animate="show"
      className="grid grid-cols-1 gap-3 sm:grid-cols-2"
    >
      {items.map(({ emoji, Icon, text }, i) => (
        <motion.li
          key={text}
          variants={item}
          data-testid={`${testidPrefix}-${i}`}
          className="group flex items-start gap-3 rounded-2xl border border-white/55 bg-white/45 p-4 backdrop-blur-md transition-all duration-300 hover:-translate-y-0.5 hover:border-rose-200 hover:bg-white/70 hover:shadow-md hover:shadow-rose-500/10"
        >
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-rose-50 text-base"
            aria-hidden="true"
          >
            <span className="leading-none">{emoji}</span>
          </span>
          <span className="mt-1 text-sm leading-relaxed text-slate-700 md:text-[15px]">
            {text}
          </span>
          {/* Icon kept for screen-readers / fallback, hidden visually since emoji is shown */}
          <Icon className="sr-only" aria-hidden="true" />
        </motion.li>
      ))}
    </motion.ul>
  );
}

export default function About() {
  return (
    <main
      data-testid="about-page"
      className="relative flex min-h-screen w-full flex-col overflow-hidden text-slate-900"
      style={{
        background:
          "linear-gradient(135deg, #FFF6F7 0%, #FDF8F5 40%, #FFEEF1 100%)",
      }}
    >
      <PageBackground />
      <Nav activePath="/about" />

      <section
        data-testid="about-section"
        className="relative z-10 mx-auto flex w-full max-w-3xl flex-1 flex-col px-5 py-10 md:px-12 md:py-16 lg:px-16"
      >
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="inline-flex w-fit items-center gap-2 rounded-full border border-rose-200/70 bg-rose-50/80 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-rose-600"
          data-testid="about-eyebrow"
        >
          About Mishenka
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
          className="font-display mt-5 text-4xl font-light leading-[1] tracking-tight text-slate-900 sm:text-5xl md:text-6xl"
          data-testid="about-heading"
        >
          Hi!
          <span className="relative ml-2 italic text-rose-500">
            I&apos;m
            <span className="absolute -bottom-1 left-0 h-[3px] w-full rounded-full bg-rose-300/60" />
          </span>
          <br />
          Mishenka.
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          className="mt-8 rounded-[24px] border border-white/60 bg-white/55 p-6 shadow-[0_20px_60px_-12px_rgba(244,63,94,0.16)] backdrop-blur-2xl backdrop-saturate-150 sm:p-8 md:p-9"
          data-testid="about-intro"
        >
          <p className="text-base font-light leading-[1.75] text-slate-700 md:text-[17px]">
            Hi! I&apos;m{" "}
            <span className="font-medium text-slate-900">Mishenka Iskandera</span>,
            a VTuber and aspiring racing driver with a passion for speed,
            motorsport, and everything powered by an engine.
          </p>
          <p className="mt-4 text-base font-light leading-[1.75] text-slate-700 md:text-[17px]">
            Whether it&apos;s racing on track, learning new skills, or sharing
            my journey online, I&apos;m always looking for the next challenge.
          </p>
        </motion.div>

        <div className="mt-6 grid grid-cols-1 gap-6 md:mt-8">
          <SectionCard title="My Goals" delay={0.3} testid="about-goals">
            <BulletList items={GOALS} testidPrefix="goal" />
          </SectionCard>

          <SectionCard title="What I Love" delay={0.45} testid="about-loves">
            <BulletList items={LOVES} testidPrefix="love" />
          </SectionCard>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.6 }}
          className="mt-10"
        >
          <Link
            to="/"
            data-testid="back-home-link"
            className="group inline-flex items-center gap-2 rounded-full border border-slate-900/15 bg-white/60 px-6 py-3 text-sm font-medium text-slate-800 backdrop-blur-md transition-all duration-300 hover:-translate-y-0.5 hover:border-rose-300/60 hover:bg-white/90 hover:text-rose-600"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-0.5" />
            Back to home
          </Link>
        </motion.div>
      </section>

      <PageFooter />
    </main>
  );
}
